import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import dot from 'dot-object';
import classNames from 'classnames';
import autocast from 'autocast';

import { fetchHomeFavorites } from '../../../src/actions/homeFavorites';

import Api from '../../api/Api';
import i18n from '../../i18n';
import Tour from '../../utils/Tour';
import Utils from '../../utils/utils';
import FiltersModel from '../../models/Filters';
import Group from '../../models/Group';
import Home from '../../models/Home';
import User from '../../models/User';
import { setDisplayAlertsComponentStatus } from '../../actions/search';
import { addSearch } from '../../actions/savedSearches';
import Pagination from '../common/Pagination';
import Alert from '../../models/Alert';
import Screen from '../../utils/Screen';
import SearchMap from './map/SearchMap';
import { PROVIDER_JAWG } from '../../constants/search';
import AutocompleteService from '../../utils/AutocompleteService';
import { IconMap } from '../icons/IconMap';
import { IconList } from '../icons/IconList';

import Filters from './Filters';
import OfflineFilters from './OfflineFilters';
import ResultCount from './ResultCount';
import Results from './Results';
import SearchFlexibilityBlock from './SearchFlexibilityBlock';

export class Search extends React.Component {
    filtersChanged = false;
    removeCountryFromFilters = false;

    static propTypes = {
        user: PropTypes.instanceOf(User),
        groups: PropTypes.arrayOf(PropTypes.instanceOf(Group)),
        alerts: PropTypes.arrayOf(PropTypes.instanceOf(Alert)),
        queryString: PropTypes.string,
        placeId: PropTypes.string,
        filters: PropTypes.object,
        offset: PropTypes.number,
        resultsCount: PropTypes.number,
        results: PropTypes.arrayOf(PropTypes.instanceOf(Home)),
        bounds: PropTypes.shape({
            ne: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
            sw: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number })
        }),
        search: PropTypes.func.isRequired,
        isLogged: PropTypes.bool,
        searchPage: PropTypes.func.isRequired,
        getNumResultsPerPage: PropTypes.func.isRequired,
        getQuery: PropTypes.func.isRequired,
        version: PropTypes.number,
        additionalSearch: PropTypes.shape({
            offset: PropTypes.number,
            results: PropTypes.arrayOf(PropTypes.instanceOf(Home)),
            resultsCount: PropTypes.number,
            bounds: PropTypes.shape({
                ne: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
                sw: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number })
            })
        }),
        lastSearchId: PropTypes.number,
        searchInProcess: PropTypes.bool,
        displaySearchInThisRegionBtn: PropTypes.bool,
        updateDisplaySearchInThisRegionBtnStatus: PropTypes.func,
        displayAlerts: PropTypes.bool,
        updateDisplayAlertsStatus: PropTypes.func,
        addAlert: PropTypes.func,
        fetchHomeFavorites: PropTypes.func,
        homeFavorites: PropTypes.arrayOf(PropTypes.number)
    };

    static defaultProps = {
        queryString: '',
        placeId: null,
        filters: {},
        offset: 0
    };

    static MAX_RESULTS_TO_SHOW_ADDITIONAL_SEARCH = 20;
    static MAIN_RESULTS = 'MAIN_RESULTS';
    static ADDITIONAL_RESULTS = 'ADDITIONAL_RESULTS';

    constructor(props) {
        super(props);

        this.page = 1;

        this.initSearch = this.initSearch.bind(this);
        this.search = this.search.bind(this);
        this.searchPage = this.searchPage.bind(this);
        this.updateFilters = this.updateFilters.bind(this);
        this.homeRollOut = this.homeRollOut.bind(this);
        this.homeRollOver = this.homeRollOver.bind(this);
        this.gotoPage = this.gotoPage.bind(this);
        this.setBounds = this.setBounds.bind(this);
        this.unZoom = this.unZoom.bind(this);
        this.shouldShowMainResults = this.shouldShowMainResults.bind(this);
        this.shouldShowAdditionalResults = this.shouldShowAdditionalResults.bind(this);
        this.onUpdatePage = this.onUpdatePage.bind(this);
        this.onSearchStart = this.onSearchStart.bind(this);
        this.onSearchSuccess = this.onSearchSuccess.bind(this);
        this.initTours = this.initTours.bind(this);
        this.removeFilter = this.removeFilter.bind(this);
        this.updateFlexibility = this.updateFlexibility.bind(this);
        this.shouldShowPushAlert = this.shouldShowPushAlert.bind(this);
        this.shouldShowPushReferral = this.shouldShowPushReferral.bind(this);
        this.updateGpRange = this.updateGpRange.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.isMapDisplayed = this.isMapDisplayed.bind(this);
        this.toggleMoreFiltersModalDisplay = this.toggleMoreFiltersModalDisplay.bind(this);

        this.state = {
            placeId: props.placeId,
            gp_range: {
                min: 0,
                max: 250
            },
            openMoreFiltersModal: false,
            filters: null,
            hoveredHomeId: null,
            displayMap: false,
            screenState: {
                noDesktop: Screen.noDesktop(),
                isTabletOrUpper: Screen.isTabletOrUpper()
            }
        };
    }

    componentDidMount() {
        $(document.body).addClass('search-body');

        if (this.props.user && this.props.user.get('is_he_collection')) {
            this.updateGpRange({ ...this.state.gp_range, max: 700 });
        }

        // header full width
        $('#head-nav-band > .navbar > .container').removeClass('container').addClass('container-fluid');

        // remove footer
        $('body > footer').remove();

        // Version is loaded or offline mode, execute search
        if (this.props.version || !this.props.isLogged) {
            this.initSearch();
        }

        // handle navigation event
        window.addEventListener('popstate', this.initSearch, false);

        // change page when changing filters in the searchbar
        document.addEventListener('update_page', this.onUpdatePage);
        // add loaders when search is processing
        document.addEventListener('search-start', this.onSearchStart);
        document.addEventListener('search-success', this.onSearchSuccess);

        if (this.props.isLogged && typeof this.props.fetchHomeFavorites === 'function') {
            this.props.fetchHomeFavorites();
        }

        this.removeResizeListener = Screen.addResizeListener((screenState) => {
            this.setState({ screenState });
        });

        this.debouncedRemoveListener = _.debounce(() => {
            this.removeResizeListener();
        }, 200);

        window.addEventListener('beforeunload', this.debouncedRemoveListener);
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.initSearch, false);
        document.removeEventListener('update_page', this.onUpdatePage);
        document.removeEventListener('search-start', this.onSearchStart);
        document.removeEventListener('search-success', this.onSearchSuccess);
        window.removeEventListener('beforeunload', this.debouncedRemoveListener);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.version && prevProps.version != this.props.version) {
            this.initSearch();
        }
        this.saveState();
        this.updateUrl(prevProps, prevState);
        if (this.state.displayMap !== prevState.displayMap && this.state.displayMap) {
            window.dispatchEvent(new Event('resize'));
        }
    }

    componentDidCatch(error, info) {
        console.error(error, info);
        Sentry.withScope((scope) => {
            scope.setExtra('info', info);
            Sentry.captureException(error);
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.filters && nextProps.filters != this.props.filters) {
            let nextFilters = nextProps.filters;
            this.setState({
                filters: this.stateFiltersFromProps(nextProps)
            });
            this.filtersChanged = true;
            if (this.searchbarFiltersHaveChanged(this.props.filters, nextFilters)) {
                // When the filters have changed from the searchbar, start a new search
                this.updateFilters(nextFilters);
            }
        }
        if (
            this.props.queryString &&
            nextProps.queryString &&
            this.props.queryString != nextProps.queryString
        ) {
            this.removeCountryFromFilters = false;
        }
    }

    initTours() {
        const { user } = this.props;

        if (user) {
            const isCollection = user.get('is_he_collection');
            Tour.isViewed(Tour.searchTour.id).then((isViewed) => {
                if (isViewed) {
                    Tour.isViewed(Tour.exchangeTypeFilterTour.id).then((exchangeTypeFilterTourIsViewed) => {
                        if (!exchangeTypeFilterTourIsViewed) {
                            Tour.init(Tour.exchangeTypeFilterTour.id);
                        }
                    });

                    if (isCollection) {
                        Tour.isViewed(Tour.collectionFilterTour.id).then((isCollectionFilterTourViewed) => {
                            if (!isCollectionFilterTourViewed) {
                                Tour.init(Tour.collectionFilterTour.id);
                            }
                        });
                    }

                    return;
                }
                Tour.init(Tour.searchTour.id);
            });
        }
    }

    onUpdatePage(e) {
        if (e && e.detail && e.detail.page) {
            this.page = e.detail.page;
        }
    }

    onSearchStart(e) {
        if (e && e.detail && e.detail.type) {
            const resultComponent = e.detail.type === 'additional' ? this.additionalResults : this.results;
            if (resultComponent) {
                resultComponent.loading(true);
            }
        }
    }

    onSearchSuccess(e) {
        if (e && e.detail && e.detail.type) {
            const resultComponent = e.detail.type === 'additional' ? this.additionalResults : this.results;

            // Scroll to the top of the result list
            this.scrollable.scrollTop = 0;

            // init search-tour
            if (e.detail.type === 'main') {
                this.initTours();
            }

            // Hide loader
            if (resultComponent) {
                resultComponent.loading(false);
            }
        }
    }

    updateGpRange(gpRange) {
        this.setState({ gp_range: gpRange });
    }

    initSearch() {
        const { user } = this.props;
        const defaultFilters = {};
        const context = Utils.getParameter('context', null);

        this.page = parseInt(Utils.getParameter('page', 1), 10);

        return this.getFiltersFromUrl(defaultFilters)
            .then(this.generateLocationFilters.bind(this))
            .then((filters) => {
                if (
                    user &&
                    user.get('is_he_collection') &&
                    user.get('he_collection_version') >= 2 &&
                    context === 'search-bar'
                ) {
                    filters.luxury = true;
                }
                this.setState({ filters });
                return filters;
            })
            .then(this.updateFilters);
    }

    search(filters, page) {
        const { lastSearchId } = this.props;
        const { placeId } = filters;
        this.props.search(
            {
                filters,
                placeId,
                lastSearchId
            },
            page
        );
    }

    searchPage(filters, page) {
        this.props.searchPage(
            {
                filters,
                placeId: filters.placeId
            },
            page
        );

        // scroll to top result
        const target = Screen.isMobile() ? window : $('.search-panel')[0];
        target.scroll(0, 0);
    }

    getFiltersFromUrl(defaultFilters) {
        return new Promise((resolve) => {
            const filters = FiltersModel.fromUrl().toJSON();
            // Add some filters by default (if not already present)
            if (defaultFilters) {
                const defaults = dot.object(Utils.deepclone(defaultFilters));
                for (const filter in defaults) {
                    const defaultFilterValue = dot.pick(filter, defaults);
                    if (Array.isArray(defaultFilterValue)) {
                        // We want to extend arrays and not replace them
                        const value = dot.pick(filter, filters) || []; // Get current value
                        defaultFilterValue.forEach((val) => {
                            // Check that the default filter is not already in the filters
                            if (value.indexOf(val) < 0) {
                                // Add to filters
                                value.push(val);
                                dot.str(filter, value, filters);
                            }
                        });
                    } else {
                        // Just set the value
                        dot.str(filter, defaultFilterValue, filters);
                    }
                }
            }
            resolve(filters);
        });
    }

    async generateLocationFilters(filters) {
        // Generate bounds filter if not defined
        if (
            !_.has(filters, 'location') ||
            (!_.has(filters.location, 'bounds') && !_.has(filters.location, 'polygon'))
        ) {
            const queryString = this.props.getQuery();
            if (queryString) {
                const result = await AutocompleteService.getPlaceSuggestions(queryString);
                const firstResult = result[0];
                return Object.assign({}, filters, {
                    location: Object.assign({}, filters.location, {
                        polygon: {
                            location_id: firstResult.properties.gid,
                            provider: PROVIDER_JAWG
                        },
                        country: firstResult.properties.country_a
                    })
                });
            } else {
                // set world bounds per default
                filters.location = Object.assign({}, filters.location, {
                    bounds: {
                        ne: {
                            lat: 85,
                            lon: 180
                        },
                        sw: {
                            lat: -85,
                            lon: -180
                        }
                    }
                });
                return Promise.resolve(filters);
            }
        } else {
            return Promise.resolve(filters);
        }
    }

    // utility method to update filters
    updateFilters(filters) {
        filters = Object.assign({}, FiltersModel.getEmptyFilters(), filters);
        // update state
        this.page = 1;
        this.search(filters);
    }

    setCenter(lat, lng) {
        this.map.setCenter(lat, lng);
    }

    setBounds(bounds) {
        // don't update bounds if map is hidden (bounds 0)
        if ($(this.mapContainer).is(':hidden')) {
            return;
        }
        if (
            _.has(this.props.filters, 'location') &&
            _.isEqual(bounds, this.props.filters.location.bounds) &&
            !this.filtersChanged
        ) {
            return;
        }
        // clone filters
        this.filtersChanged = false;
        const filters = JSON.parse(JSON.stringify(this.props.filters));
        filters.location = filters.location || {};
        filters.location.bounds = bounds;
        delete filters.location.polygon;
        delete filters.location.center;
        delete filters.location.city;
        if (this.removeCountryFromFilters) {
            delete filters.country;
            delete filters.location.country;
        }
        // update state
        this.page = 1;
        this.search(filters);
        this.removeCountryFromFilters = true;
    }

    saveState() {
        if (this.props.user) {
            const data = {
                place: this.props.queryString,
                query: this.props.filters,
                offset: this.props.offset
            };
            Api.Search.setLastSearch(this.props.user.id, data);
        }
    }

    updateUrl(prevProps, prevState) {
        const prevFilters = new FiltersModel(prevProps.filters);
        const filters = new FiltersModel(this.props.filters);
        if (
            prevState.placeId === this.state.placeId &&
            this.props.offset === prevProps.offset &&
            this.props.additionalSearch.offset === prevProps.additionalSearch.offset &&
            _.isEqual(prevFilters.params(), filters.params())
        ) {
            // no changes
            return;
        }
        const queryString = this.props.queryString ? this.props.queryString : this.props.getQuery();
        const path = queryString
            ? i18n.t('search:search_url', {
                  slug: encodeURIComponent(queryString)
              })
            : document.location.pathname;

        let params = filters.params();
        if (this.page > 1) {
            params.page = this.page;
        }

        params = $.param(params);
        const url = path + (params ? `?${params}` : '');

        if (document.location.search !== params) {
            if (
                prevState.placeId &&
                (!_.isEmpty(prevProps.filters) || this.props.offset != prevProps.offset)
            ) {
                history.pushState(null, null, url);
            } else {
                history.replaceState(null, null, url);
            }
        }
    }

    homeRollOver(home) {
        this.setState({ hoveredHomeId: home.get('id') });
    }

    homeRollOut() {
        this.setState({ hoveredHomeId: null });
    }

    getNbPages() {
        const totalResults = this.props.resultsCount + this.props.additionalSearch.resultsCount;
        return Math.ceil(totalResults / this.props.getNumResultsPerPage());
    }

    gotoPage(page) {
        this.page = page;

        this.searchPage(this.props.filters, page);
    }

    shouldShowMainResults(page = this.page) {
        return (
            _.isNull(this.props.resultsCount) ||
            this.props.resultsCount > (page - 1) * this.props.getNumResultsPerPage() ||
            (this.props.resultsCount === 0 &&
                (!this.shouldShowAdditionalResults() || this.props.additionalSearch.resultsCount === 0))
        );
    }

    shouldShowAdditionalResults(page = this.page) {
        return (
            !_.isNull(this.props.resultsCount) &&
            this.props.resultsCount < Search.MAX_RESULTS_TO_SHOW_ADDITIONAL_SEARCH &&
            this.props.resultsCount <= page * this.props.getNumResultsPerPage() &&
            (_.isNull(this.props.additionalSearch.resultsCount) || // Results not yet loaded
                this.props.additionalSearch.resultsCount > 0)
        );
    }

    shouldShowPushReferral() {
        const { user } = this.props;
        return user && !user.get('is_he_collection') && user.get('has_finalized_exchange_in_last_30_days');
    }

    shouldShowPushAlert(origin) {
        const { resultsCount } = this.props;

        if (this.shouldShowPushReferral()) {
            return false;
        }

        return origin === Search.MAIN_RESULTS ? resultsCount > 0 : resultsCount === 0;
    }

    shouldShowMainTitle(page = this.page) {
        // See https://www.notion.so/tukazza/Search-additional-search-if-few-results-fd85e4374fc3461bb41fafc9126ef53f
        return (
            !_.isNull(this.props.resultsCount) &&
            this.props.resultsCount > 0 &&
            this.props.resultsCount < Search.MAX_RESULTS_TO_SHOW_ADDITIONAL_SEARCH &&
            this.props.additionalSearch.resultsCount > 0 &&
            this.props.resultsCount > (page - 1) * this.props.getNumResultsPerPage()
        );
    }

    shouldShowAdditionalTitle(page = this.page) {
        // See https://www.notion.so/tukazza/Search-additional-search-if-few-results-fd85e4374fc3461bb41fafc9126ef53f
        // Hide title if there are no mainTitle on this page
        return (
            this.props.resultsCount > (page - 1) * this.props.getNumResultsPerPage() ||
            (this.props.resultsCount === 0 && this.props.additionalSearch.resultsCount > 0)
        );
    }

    unZoom(scale) {
        if (this.searchMapRef) {
            this.searchMapRef.unzoom(scale);
        }
    }

    hasDates() {
        return Boolean(
            this.props.filters.calendar?.date_range &&
                this.props.filters.calendar.date_ranges.length > 0 &&
                this.props.filters.calendar.date_ranges[0].from &&
                this.props.filters.calendar.date_ranges[0].to
        );
    }

    toggleMoreFiltersModalDisplay() {
        this.setState({
            openMoreFiltersModal: !this.state.openMoreFiltersModal
        });
    }

    searchbarFiltersHaveChanged(oldFilters, newFilters) {
        if (_.isEmpty(oldFilters) || (!oldFilters && !newFilters)) {
            // No filters
            return false;
        } else if (!oldFilters && newFilters) {
            // Filters for the first time
            return true;
        } else if (JSON.stringify(oldFilters.home) !== JSON.stringify(newFilters.home)) {
            // Guest number has changed
            return true;
        } else if (
            (!oldFilters.location && newFilters.location) ||
            (oldFilters.location && !newFilters.location) ||
            (oldFilters.location &&
                newFilters.location &&
                (!_.isEqual(oldFilters.location.bounds, newFilters.location.bounds) ||
                    !_.isEqual(oldFilters.location.polygon, newFilters.location.polygon)))
        ) {
            return true;
        } else if (!_.isEqual(oldFilters.calendar, newFilters.calendar)) {
            // Dates have changed
            return true;
        }

        return false;
    }

    stateFiltersFromProps(props) {
        const filters = new FiltersModel(props.filters);
        return filters.flatify();
    }

    filtersFromState() {
        const filters = FiltersModel.fromFlatFilters(this.state.filters);
        filters.set('location', Object.assign({}, this.props.filters.location, filters.get('location')));
        filters.set('calendar', Object.assign({}, this.state.filters.calendar));
        return filters.toJSON();
    }

    removeFilter(filter, value) {
        let { filters } = this.state;

        if (Array.isArray(filters[filter])) {
            const newFilter = {};
            value = _.isBoolean(value) ? value : autocast(value);
            newFilter[filter] = _.without(filters[filter], value);
            filters = Object.assign({}, filters, newFilter);
        } else if (['reciprocal', 'guest-wanted', 'available'].includes(value)) {
            const calendar = Object.assign({}, filters.calendar);
            calendar.exchange_types = _.without(calendar.exchange_types, value);
            filters = Object.assign({}, filters, { calendar });
        } else {
            delete filters[filter];
        }

        this.setState({ filters }, () => {
            this.updateFilters(this.filtersFromState());
        });
    }

    updateFlexibility(value) {
        const filters = {
            availability: {
                date_range: {
                    flexibility: parseInt(value, 10)
                }
            }
        };

        if (
            this.state.filters.availability &&
            this.state.filters.availability.date_range &&
            this.state.filters.availability.date_range.from &&
            this.state.filters.availability.date_range.to
        ) {
            const filtersCopy = JSON.parse(JSON.stringify(this.state.filters));
            filtersCopy.availability.date_range = Object.assign(
                {},
                this.state.filters.availability.date_range,
                filters.availability.date_range
            );

            this.setState({ filters: filtersCopy }, () => {
                this.updateFilters(this.filtersFromState());
            });
        }
    }

    calculateTotalResults() {
        let totalResults = this.props.resultsCount;

        if (
            this.props.resultsCount < Search.MAX_RESULTS_TO_SHOW_ADDITIONAL_SEARCH &&
            this.props.additionalSearch.resultsCount
        ) {
            totalResults += this.props.additionalSearch.resultsCount;
        }

        return totalResults;
    }

    getSearchPanelStyle() {
        return {
            display: Screen.noDesktop() && this.state.displayMap ? 'none' : 'block'
        };
    }

    handleClick() {
        this.setState({
            displayMap: !this.state.displayMap
        });
    }

    isMapDisplayed() {
        return this.mapContainer?.style.display === 'block';
    }

    render() {
        const {
            bounds,
            additionalSearch,
            searchInProcess,
            displaySearchInThisRegionBtn,
            updateDisplaySearchInThisRegionBtnStatus,
            updateDisplayAlertsStatus,
            isLogged
        } = this.props;
        const { openMoreFiltersModal, displayMap, hoveredHomeId, screenState } = this.state;

        if (!this.state.filters) {
            return null;
        }

        const totalResults = this.calculateTotalResults();

        return (
            <div className="row">
                <div
                    className={classNames('col-xs-12 col-md-8 col-lg-7 search-panel', {
                        'disable-scroll': openMoreFiltersModal
                    })}
                    style={this.getSearchPanelStyle()}
                >
                    {this.props.isLogged ? (
                        <Filters
                            ref={(filters) => {
                                this.filters = filters;
                            }}
                            user={this.props.user}
                            groups={this.props.groups}
                            filters={this.props.filters}
                            gpRange={this.state.gp_range}
                            updateFilters={this.updateFilters}
                            query={this.props.queryString}
                            resultsCount={totalResults}
                            openMoreFiltersModal={openMoreFiltersModal}
                            toggleMoreFiltersModalDisplay={this.toggleMoreFiltersModalDisplay}
                            displayAlerts={this.props.displayAlerts}
                            onUpdateFlexibility={this.updateFlexibility}
                            onRemoveFilter={this.removeFilter}
                            displayMap={displayMap}
                        />
                    ) : (
                        <OfflineFilters />
                    )}

                    <div
                        ref={(node) => {
                            this.scrollable = node;
                        }}
                        className="search-scrollable col-xs-12"
                    >
                        <div className="search-results-container" style={this.getSearchPanelStyle()}>
                            {this.shouldShowMainTitle() && (
                                <span className="title-with-results">
                                    <h4>
                                        {i18n.t(
                                            `search:main-search.${this.hasDates() ? 'title-dates' : 'title'}`
                                        )}
                                    </h4>
                                    <ResultCount count={this.props.resultsCount} />
                                </span>
                            )}
                            {this.shouldShowMainResults() && (
                                <Results
                                    ref={(results) => {
                                        this.results = results;
                                    }}
                                    results={this.props.results}
                                    alerts={this.props.alerts}
                                    unZoom={this.unZoom}
                                    homeRollOut={this.homeRollOut}
                                    homeRollOver={this.homeRollOver}
                                    isLogged={this.props.isLogged}
                                    queryString={this.props.queryString}
                                    filters={this.state.filters}
                                    showPushAlert={this.shouldShowPushAlert(Search.MAIN_RESULTS)}
                                    showPushReferral={this.shouldShowPushReferral()}
                                    excludeFilters={['availability', 'bounds', 'home', 'placeId']}
                                    onRemoveFilter={this.removeFilter}
                                    onUpdateFlexibility={this.updateFlexibility}
                                    addAlert={this.props.addAlert}
                                    user={this.props.user}
                                />
                            )}

                            {this.shouldShowAdditionalResults() && (
                                <div className="additional-search-results">
                                    <hr />
                                    {this.shouldShowAdditionalTitle() && (
                                        <div className="title-with-results additional-results">
                                            <div>
                                                <h4>
                                                    {i18n.t(
                                                        `search:additional-search.${
                                                            this.hasDates() ? 'title-dates' : 'title'
                                                        }`
                                                    )}
                                                </h4>
                                                <p
                                                    dangerouslySetInnerHTML={{
                                                        __html: i18n.t('search:additional-search.description')
                                                    }}
                                                />
                                            </div>
                                            <ResultCount count={this.props.additionalSearch.resultsCount} />
                                        </div>
                                    )}
                                    <Results
                                        ref={(ref) => {
                                            this.additionalResults = ref;
                                        }}
                                        results={this.props.additionalSearch.results}
                                        homeRollOut={this.homeRollOut}
                                        homeRollOver={this.homeRollOver}
                                        isLogged={this.props.isLogged}
                                        queryString={this.props.queryString}
                                        filters={this.state.filters}
                                        showPushReferral={this.shouldShowPushReferral()}
                                        showPushAlert={this.shouldShowPushAlert(Search.ADDITIONAL_RESULTS)}
                                        addAlert={this.props.addAlert}
                                        user={this.props.user}
                                    />
                                </div>
                            )}
                        </div>

                        {this.props.resultsCount > 0 && (
                            <SearchFlexibilityBlock
                                updateFilters={this.updateFilters}
                                filters={this.props.filters}
                            />
                        )}

                        <Pagination
                            page={this.page}
                            limit={this.props.getNumResultsPerPage()}
                            nbresults={totalResults}
                            gotoPage={this.gotoPage}
                        />
                    </div>
                </div>
                {screenState.noDesktop && (
                    <div className="button-mobile-map">
                        <button onClick={this.handleClick} className="btn-map">
                            <span className="btn-map-title">
                                {i18n.t(`search:${displayMap ? 'list' : 'map'}-title-button`)}
                            </span>
                            <span>{displayMap ? <IconList /> : <IconMap />}</span>
                        </button>
                    </div>
                )}
                <div
                    ref={(node) => {
                        this.mapContainer = node;
                    }}
                    className="col-xs-12 col-md-4 col-lg-5 search-map-container"
                    style={{
                        display:
                            (Screen.noDesktop() && displayMap) || Screen.isTabletOrUpper() ? 'block' : 'none'
                    }}
                >
                    {this.state.filters && (bounds || additionalSearch?.bounds) && (
                        <SearchMap
                            ref={(searchMapRef) => {
                                this.searchMapRef = searchMapRef;
                            }}
                            bounds={bounds || additionalSearch?.bounds}
                            results={this.shouldShowMainResults() ? this.props?.results : []}
                            additionalResults={
                                this.shouldShowAdditionalResults() ? this.props.additionalSearch.results : []
                            }
                            hoveredHomeId={hoveredHomeId}
                            onBoundsChange={this.setBounds}
                            displaySearchInThisRegionBtn={displaySearchInThisRegionBtn}
                            searchInProcess={searchInProcess}
                            updateDisplaySearchInThisRegionBtnStatus={
                                updateDisplaySearchInThisRegionBtnStatus
                            }
                            updateDisplayAlertsStatus={updateDisplayAlertsStatus}
                            isLogged={isLogged}
                            isMapDisplayed={this.isMapDisplayed}
                            displayMap={displayMap}
                            homeFavorites={this.props.homeFavorites}
                        />
                    )}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    const version = state.roulette.hasOwnProperty('filteredSearch')
        ? Number(state.roulette.filteredSearch)
        : null;

    const props = {
        ...state.search.main,
        additionalSearch: { ...state.search.additional },
        user: state.user,
        lastSearchId: state.search.lastSearchId,
        groups: state.user ? state.userGroups[state.user.id] : [],
        version,
        isLogged: Boolean(state.auth.accessToken),
        alerts: state.savedSearches,
        displayAlerts: state.search.displayAlertsComponent,
        homeFavorites: state.homeFavorites
    };

    if (props.results) {
        props.results = props.results.map((home) => new Home(home));
    }

    if (props.additionalSearch.results) {
        props.additionalSearch.results = props.additionalSearch.results.map((home) => new Home(home));
    }

    return props;
}

const mapDispatchToProps = (dispatch) => ({
    updateDisplayAlertsStatus: bindActionCreators(setDisplayAlertsComponentStatus, dispatch),
    addAlert: bindActionCreators(addSearch, dispatch),
    fetchHomeFavorites: bindActionCreators(fetchHomeFavorites, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Search);
